// Détection des poussières et objets isolés autour de l'échantillon, sur un niveau réduit de la pyramide :
// 1. maximum des fluorescences, chacune normalisée par son contraste (un objet visible dans une seule suffit), lissé ;
// 2. seuil bas, juste au-dessus du fond (3 × le 20e centile, au moins 0,01) : tout ce qui n'est pas du fond ;
// 3. dilatation, qui relie les fragments de tissu proches, et comblement des trous (fond non relié au bord) ;
// 4. composantes connexes : celles plus petites que keepFraction × la plus grande sont des poussières ou objets,
//    renvoyées comme polygones (bornes de chaque ligne, petite marge, simplifiés) en pixels pleine résolution, à
//    effacer comme les zones tracées à la main ; une zone qui recouvrirait du tissu gardé est écartée.
// Mis au point sur 215149H (405 / 470) : un seul bloc de tissu, ~320 objets autour ; stable pour un seuil de 0,008
// à 0,03.
import { pixelSize } from '../../services';
import { gaussBlur } from './blur';

// Niveau le plus réduit ayant au moins minPixels pixels (assez fin pour garder les petites poussières).
function pickLevel(loader, minPixels) {
  let level = loader.length - 1;
  while (level > 0) {
    const { width, height } = pixelSize(loader[level]);
    if (width * height >= minPixels) break;
    level -= 1;
  }
  return level;
}

// Seuil juste au-dessus du fond : 3 × le 20e centile des valeurs non nulles (fond quasi noir des fluorescences),
// au moins 0,01.
function backgroundThreshold(values, sample = 200000) {
  const step = Math.max(1, Math.floor(values.length / sample));
  const picked = [];
  for (let i = 0; i < values.length; i += step) if (values[i] > 0) picked.push(values[i]);
  if (!picked.length) return 0.01;
  picked.sort((a, b) => a - b);
  return Math.max(0.01, 3 * picked[Math.floor(picked.length * 0.2)]);
}

// Comble les trous d'un masque : fond (4-voisinage) non relié au bord de l'image.
export function fillHoles(mask, w, h) {
  const outside = new Uint8Array(mask.length);
  const stack = new Int32Array(mask.length);
  let top = 0;
  const push = p => { if (!mask[p] && !outside[p]) { outside[p] = 1; stack[top++] = p; } };
  for (let x = 0; x < w; x += 1) { push(x); push((h - 1) * w + x); }
  for (let y = 0; y < h; y += 1) { push(y * w); push(y * w + w - 1); }
  while (top) {
    const p = stack[--top];
    const x = p % w;
    if (x > 0) push(p - 1);
    if (x < w - 1) push(p + 1);
    if (p >= w) push(p - w);
    if (p < mask.length - w) push(p + w);
  }
  for (let i = 0; i < mask.length; i += 1) if (!outside[i]) mask[i] = 1;
  return mask;
}

// Dilatation d'un masque binaire par un carré de côté 2r + 1 (deux passes séparables).
function dilate(mask, w, h, r) {
  const tmp = new Uint8Array(mask.length);
  const out = new Uint8Array(mask.length);
  for (let y = 0; y < h; y += 1) {
    let last = -Infinity;
    for (let x = 0; x < w; x += 1) {
      if (mask[y * w + x]) last = x;
      if (x - last <= r) tmp[y * w + x] = 1;
    }
    last = Infinity;
    for (let x = w - 1; x >= 0; x -= 1) {
      if (mask[y * w + x]) last = x;
      if (last - x <= r) tmp[y * w + x] = 1;
    }
  }
  for (let x = 0; x < w; x += 1) {
    let last = -Infinity;
    for (let y = 0; y < h; y += 1) {
      if (tmp[y * w + x]) last = y;
      if (y - last <= r) out[y * w + x] = 1;
    }
    last = Infinity;
    for (let y = h - 1; y >= 0; y -= 1) {
      if (tmp[y * w + x]) last = y;
      if (last - y <= r) out[y * w + x] = 1;
    }
  }
  return out;
}

// Composantes connexes (8-voisinage) : étiquettes, et pour chacune sa surface et ses bornes par ligne.
function components(mask, w, h) {
  const labels = new Int32Array(mask.length);
  const comps = [];
  const stack = new Int32Array(mask.length);
  for (let start = 0; start < mask.length; start += 1) {
    if (!mask[start] || labels[start]) continue;
    const id = comps.length + 1;
    const rows = new Map();  // y -> [xmin, xmax]
    let area = 0;
    let top = 0;
    stack[top++] = start;
    labels[start] = id;
    while (top) {
      const p = stack[--top];
      const x = p % w;
      const y = (p - x) / w;
      area += 1;
      const row = rows.get(y);
      if (!row) rows.set(y, [x, x]);
      else { if (x < row[0]) row[0] = x; if (x > row[1]) row[1] = x; }
      for (let dy = -1; dy <= 1; dy += 1) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -1; dx <= 1; dx += 1) {
          const xx = x + dx;
          if (xx < 0 || xx >= w) continue;
          const q = yy * w + xx;
          if (mask[q] && !labels[q]) { labels[q] = id; stack[top++] = q; }
        }
      }
    }
    comps.push({ id, area, rows });
  }
  return comps;
}

// Polygone couvrant une composante : bord gauche de haut en bas puis bord droit de bas en haut (bornes de chaque
// ligne, coins des pixels), agrandi de margin px. null s'il recouvrirait du tissu gardé (keep) dans une de ses lignes.
function componentPolygon(comp, margin, keep, w) {
  const ys = [...comp.rows.keys()].sort((a, b) => a - b);
  for (const y of ys) {
    const [x0, x1] = comp.rows.get(y);
    for (let x = x0; x <= x1; x += 1) if (keep[y * w + x]) return null;
  }
  const left = [];
  const right = [];
  ys.forEach((y, i) => {
    const [x0, x1] = comp.rows.get(y);
    const y0 = i === 0 ? y - margin : y;
    const y1 = i === ys.length - 1 ? y + 1 + margin : y + 1;
    left.push([x0 - margin, y0], [x0 - margin, y1]);
    right.push([x1 + 1 + margin, y0], [x1 + 1 + margin, y1]);
  });
  return left.concat(right.reverse());
}

// channels : [{ loader, contrast }] de mêmes dimensions. Renvoie { zones: [[x, y]…][] (px pleine résolution),
// kept (blocs de tissu gardés), threshold, level }. maxPoints borne la taille enregistrée (document Firestore).
export async function detectDust(channels, { keepFraction = 0.02, minPixels = 4e6, maxPoints = 15000 } = {}) {
  if (!channels.length) throw new Error('No fluorescence image to detect dust on.');
  const level = pickLevel(channels[0].loader, minPixels);
  const { width: w, height: h } = pixelSize(channels[0].loader[level]);
  const { width: fullWidth } = pixelSize(channels[0].loader[0]);
  const scale = fullWidth / w;
  const selection = { t: 0, z: 0, c: 0 };
  const rasters = await Promise.all(channels.map(ch => ch.loader[level].getRaster({ selection })));
  const values = new Float32Array(w * h);
  rasters.forEach((raster, c) => {
    const [lo, hi] = channels[c].contrast;
    const k = 1 / ((hi - lo) || 1);
    for (let i = 0; i < values.length; i += 1) {
      const v = Math.min(1, Math.max(0, (raster.data[i] - lo) * k));
      if (v > values[i]) values[i] = v;
    }
  });
  const smooth = gaussBlur(values, w, h, 2);
  const threshold = backgroundThreshold(smooth);
  const fg = new Uint8Array(values.length);
  for (let i = 0; i < values.length; i += 1) fg[i] = smooth[i] > threshold ? 1 : 0;
  const mask = fillHoles(dilate(fg, w, h, 3), w, h);
  const comps = components(mask, w, h);
  if (!comps.length) return { zones: [], kept: 0, threshold, level };
  const largest = Math.max(...comps.map(c => c.area));
  const kept = comps.filter(c => c.area >= keepFraction * largest);
  const keep = new Uint8Array(mask.length);
  kept.forEach(c => c.rows.forEach(([x0, x1], y) => keep.fill(1, y * w + x0, y * w + x1 + 1)));
  const small = comps.filter(c => c.area < keepFraction * largest).sort((a, b) => b.area - a.area);
  const raw = small.map(c => componentPolygon(c, 1, keep, w)).filter(Boolean);
  // Simplification, plus forte si les zones dépassent maxPoints points au total.
  let zones = raw;
  for (let tol = 0.75; tol <= 12; tol *= 2) {
    zones = raw.map(z => simplifyPath(z, tol)).filter(z => z.length >= 3);
    if (zones.reduce((n, z) => n + z.length, 0) <= maxPoints) break;
  }
  return { zones: zones.map(z => z.map(([x, y]) => [Math.round(x * scale), Math.round(y * scale)])), kept: kept.length, threshold, level };
}

// ------------------------------------------------------------------ géométrie des zones effacées

// Point (x, y) dans le polygone [[x, y]…] (règle pair-impair).
export function pointInPolygon(poly, x, y) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

// Surface d'un polygone (formule du lacet).
export function polygonArea(poly) {
  let s = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) s += (poly[j][0] + poly[i][0]) * (poly[j][1] - poly[i][1]);
  return Math.abs(s) / 2;
}

// Tracé simplifié (Ramer-Douglas-Peucker) : points à moins de tol de la corde retirés.
export function simplifyPath(points, tol) {
  if (points.length < 3) return points;
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop();
    const [ax, ay] = points[a];
    const [bx, by] = points[b];
    const len = Math.hypot(bx - ax, by - ay) || 1;
    let max = 0;
    let index = -1;
    for (let i = a + 1; i < b; i += 1) {
      const [px, py] = points[i];
      const d = Math.abs((by - ay) * px - (bx - ax) * py + bx * ay - by * ax) / len;
      if (d > max) { max = d; index = i; }
    }
    if (max > tol) { keep[index] = 1; stack.push([a, index], [index, b]); }
  }
  return points.filter((_, i) => keep[i]);
}
